import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Menu, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { img, inr } from "@/lib/api";
import { Logo } from "@/components/Logo";

const NAV = [
  { to: "/#edit", label: "The Edit" },
  { to: "/shop", label: "Shop all" },
  { to: "/#story", label: "Our Story" },
];

const BagIcon = () => {
  const { count, setBagOpen } = useStore();
  return (
    <button onClick={() => setBagOpen(true)} data-testid="header-bag-button" aria-label="Open bag" className="relative grid place-items-center w-11 h-11 rounded-[14px] hover:bg-plum/5 transition-colors">
      <ShoppingBag className="w-[22px] h-[22px]" strokeWidth={1.8} />
      <AnimatePresence>
        {count > 0 && (
          <motion.span
            key={count}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: [1.35, 1], opacity: 1 }}
            transition={{ duration: 0.45 }}
            data-testid="header-bag-count"
            className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-berry text-pearl text-[11px] font-extrabold grid place-items-center"
          >
            {count}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
};

const MobileMenu = ({ open, setOpen }) => {
  const { products } = useStore();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="left" className="w-[88vw] max-w-[380px] bg-pearl p-6 rounded-r-[22px] border-0">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="mt-2"><Logo /></div>
        <nav className="mt-10 flex flex-col">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.replace(/\s/g, "-").toLowerCase()}`} className="font-display text-[34px] leading-tight py-2 border-b border-plum/10">
              {n.label}
            </Link>
          ))}
          <Link to="/wishlist" onClick={() => setOpen(false)} data-testid="mobile-nav-wishlist" className="font-display text-[34px] leading-tight py-2 border-b border-plum/10">Wishlist</Link>
        </nav>
        <p className="eyebrow text-berry mt-10 mb-4">The Selected Three</p>
        <div className="grid grid-cols-3 gap-2">
          {products.slice(0, 3).map((p) => (
            <Link key={p.id} to={`/product/${p.slug}`} onClick={() => setOpen(false)} className="block">
              <img src={img(p.images[0])} alt={p.name} className="aspect-square w-full object-cover rounded-[14px]" />
              <p className="mt-1.5 text-[12px] font-bold leading-tight">{inr(p.effective_price)}</p>
            </Link>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => {
  const { site, wish } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const free = site?.shipping.free_threshold;

  return (
    <header className="sticky top-0 z-40" data-testid="site-header">
      <div className="bg-plum text-pearl text-[13px] font-semibold text-center py-2 px-4 tracking-wide">
        {free > 0 ? `Free shipping above ${inr(free)}` : "Free shipping across India"} <span className="text-champagne mx-1.5">◆</span> Pay securely by UPI
      </div>
      <div className={`transition-colors duration-300 ${scrolled ? "bg-pearl/85 backdrop-blur-xl border-b border-plum/10" : "bg-pearl border-b border-transparent"}`}>
        <div className="container-rh h-[68px] lg:h-[76px] grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="flex items-center">
            <button className="lg:hidden grid place-items-center w-11 h-11 -ml-2 rounded-[14px]" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-button">
              <Menu className="w-6 h-6" />
            </button>
            <nav className="hidden lg:flex items-center gap-9">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.label.replace(/\s/g, "-").toLowerCase()}`} className="text-[13px] font-bold uppercase tracking-[0.16em] hover:text-berry transition-colors">
                  {n.label}
                </Link>
              ))}
            </nav>
          </div>
          <Logo />
          <div className="flex items-center justify-end gap-1">
            <Link to="/wishlist" data-testid="header-wishlist-link" aria-label="Wishlist" className="relative hidden sm:grid place-items-center w-11 h-11 rounded-[14px] hover:bg-plum/5">
              <Heart className="w-[21px] h-[21px]" strokeWidth={1.8} />
              {wish.length > 0 && <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-ruby" />}
            </Link>
            <BagIcon />
          </div>
        </div>
      </div>
      <MobileMenu open={open} setOpen={setOpen} />
    </header>
  );
};
